import { Month } from './Month';
import { defaults, classes } from '../defaults';
import { createElement, dateClamp, first, numberClamp } from '../utils';

import addDays from 'date-fns/addDays';
import addMonths from 'date-fns/addMonths';
import differenceInCalendarMonths from 'date-fns/differenceInCalendarMonths';
import eachMonthOfInterval from 'date-fns/eachMonthOfInterval';
import isSameDay from 'date-fns/isSameDay';
import isSameMonth from 'date-fns/isSameMonth';
import startOfDay from 'date-fns/startOfDay';
import subDays from 'date-fns/subDays';
import subMonths from 'date-fns/subMonths';

export function DatePicker(options) {
	let config = {
		...defaults,
		...options,
		classes: { ...classes, ...options.classes },
	};

	config.date = startOfDay(config.date);
	config.minDate = startOfDay(config.minDate);
	config.maxDate = startOfDay(config.maxDate);

	let state = {
		days: [],
		months: [],
		focusedDay: undefined,
	};

	let monthsElement = createElement('div');
	let { prevMonthButton, nextMonthButton } = createUI();

	monthsElement.className = config.classes.months;

	prevMonthButton.addEventListener('click', () => prev());
	nextMonthButton.addEventListener('click', () => next());

	prevMonthButton.addEventListener('keydown', handleNavKeydown);
	nextMonthButton.addEventListener('keydown', handleNavKeydown);

	config.container.classList.add(config.classes.root);
	config.container.append(prevMonthButton, nextMonthButton, monthsElement);

	render();
	registerEventListeners();

	function render(date = config.date, { focus = false } = {}) {
		state.days = [];
		state.months = [];
		config.container.classList.add(`${config.classes.root}--${config.numberOfMonths}-months`);

		monthsElement.innerHTML = '';

		let months = getMonths(date);

		months.forEach(month => {
			let { render, days, focusDate } = Month(month, config);

			state.days.push(...days);
			state.months.push(month);

			monthsElement.append(render());

			if (isSameMonth(date, month) && focus) {
				focusDate(date);
			}
		});

		config.date = date;
	}

	function goTo(date, focus = false) {
		render(date, { focus });
	}

	function next(step = 1, opts) {
		let { pagedNavigation, numberOfMonths } = config;
		let n = pagedNavigation ? numberOfMonths : step;

		render(addMonths(first(state.months), n), opts);
	}

	function prev(step = 1, opts) {
		let { pagedNavigation, numberOfMonths } = config;
		let n = pagedNavigation ? numberOfMonths : step;

		render(subMonths(first(state.months), n), opts);
	}

	function getMonths(date) {
		const { minDate, maxDate, numberOfMonths } = config;

		/**
		 * We respect the numberOfMonths param even when the difference between
		 * minDate and maxDate are less than numberOfMonths. In this case we
		 * show the minDate + n months.
		 */
		if (differenceInCalendarMonths(maxDate, minDate) < numberOfMonths) {
			return eachMonthOfInterval({
				start: minDate,
				end: addMonths(minDate, numberOfMonths - 1),
			});
		}

		let clampedDate = monthClamp(date);

		let diff = differenceInCalendarMonths(maxDate, clampedDate);

		let endPadding = numberClamp(diff, 0, numberOfMonths - 1);
		let startPadding = numberOfMonths - 1 - endPadding;

		function monthClamp(date) {
			return dateClamp(date, minDate, maxDate);
		}

		return eachMonthOfInterval({
			end: monthClamp(addMonths(clampedDate, endPadding)),
			start: monthClamp(subMonths(clampedDate, startPadding)),
		});
	}

	function registerEventListeners() {
		config.container.addEventListener('dayClick', handleDayClick);
		config.container.addEventListener('dayFocus', handleDayFocus);
		config.container.addEventListener('dayKeydown', handleDayKeydown);
		config.container.addEventListener('dayMouseover', handleDayMouseover);
	}

	function handleDayClick(event) {
		event.detail.focus();

		config.onDayClick({ ...event.detail });
	}

	function handleDayMouseover(event) {
		config.onDayMouseover({ ...event.detail });
	}

	function handleDayFocus(event) {
		if (state.focusedDay && (state.focusedDay.element !== event.detail.element)) {
			state.focusedDay.blur();
		}

		state.focusedDay = event.detail;
	}

	function handleDayKeydown(event) {
		let handled = false;
		let {
			date,
			event: nativeEvent,
		} = event.detail;

		switch (nativeEvent.key) {
			case 'ArrowLeft':
				handled = true;
				focus(subDays(date, 1));
				break;
			case 'ArrowRight':
				handled = true;
				focus(addDays(date, 1));
				break;
			case 'ArrowUp':
				handled = true;
				focus(subDays(date, 7));
				break;
			case 'ArrowDown':
				handled = true;
				focus(addDays(date, 7));
				break;
		}

		if (handled) {
			nativeEvent.preventDefault();
		}

		function focus(date) {
			let days = state.days.filter(isFocusable);
			let day = days.find(day => isSameDay(day.date, date));

			if (day) {
				return day.focus();
			}

			render(date, { focus: true });

			function isFocusable(day) {
				return !day.modifiers.outside && !day.modifiers.hidden;
			}
		}
	}

	function handleNavKeydown(event) {
		if (event.key === 'ArrowLeft') {
			prev();
		} else if (event.key === 'ArrowRight') {
			next();
		}
	}

	function set(key, value) {
		config[key] = value;

		if (!['disabled', 'selected', 'modifiers'].includes(key)) {
			render();
		} else {
			state.days.forEach(d => d.render());
		}
	}

	function createUI() {
		const nextMonthButton = createElement('button');
		const prevMonthButton = createElement('button');

		nextMonthButton.classList.add(config.classes.next);
		prevMonthButton.classList.add(config.classes.prev);

		nextMonthButton.setAttribute('aria-label', 'Next month');
		prevMonthButton.setAttribute('aria-label', 'Previous month');

		return {
			prevMonthButton,
			nextMonthButton,
		};
	}

	return { goTo, next, prev, set };
}
